/**
 * Galeria de anexos Customers da OS — painel inline no scroll do detalhe V2.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Expand, FileText, Minus, Plus, X } from 'lucide-react';
import './OrderDetailsAnexosModal.css';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

function clampZoom(n) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n * 100) / 100));
}

function AnexosZoomViewer({ src, alt }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };

  const onPointerUp = (e) => {
    if (dragRef.current && e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <>
      <div className="odv2-anexos-zoom-toolbar">
        <button
          type="button"
          aria-label="Diminuir zoom"
          disabled={scale <= ZOOM_MIN}
          onClick={() => setScale((s) => clampZoom(s - ZOOM_STEP))}
        >
          <Minus size={16} strokeWidth={2} aria-hidden />
        </button>
        <span>{Math.round(scale * 100)}%</span>
        <button
          type="button"
          aria-label="Aumentar zoom"
          disabled={scale >= ZOOM_MAX}
          onClick={() => setScale((s) => clampZoom(s + ZOOM_STEP))}
        >
          <Plus size={16} strokeWidth={2} aria-hidden />
        </button>
      </div>
      <div
        className={`odv2-anexos-zoom-viewport${dragging ? ' odv2-anexos-zoom-viewport--dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          }}
        />
      </div>
    </>
  );
}

export function OrderDetailsAnexosModal({ open, files = [], loading = false, error = null }) {
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setSelected(0);
    setExpanded(false);
  }, [open, files]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setExpanded(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [expanded]);

  if (!open) return null;

  const current = files[selected] || null;

  const zoomNode =
    expanded && current && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="odv2-anexos-zoom-overlay"
            role="presentation"
            onClick={() => setExpanded(false)}
          >
            <div
              className="odv2-anexos-zoom-modal"
              role="dialog"
              aria-label={current.name}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="odv2-anexos-zoom-close"
                aria-label="Fechar ampliação"
                onClick={() => setExpanded(false)}
              >
                <X size={18} strokeWidth={2} aria-hidden />
              </button>
              {current.isImage ? (
                <AnexosZoomViewer src={current.url} alt={current.name} />
              ) : String(current.ext).toLowerCase() === 'pdf' ? (
                <iframe title={current.name} src={current.url} />
              ) : (
                <a href={current.url} target="_blank" rel="noreferrer">
                  Abrir arquivo
                </a>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="odv2-anexos-panel" role="region" aria-label="Anexos da OS">
      {loading ? (
        <p className="odv2-anexos-status">Carregando anexos…</p>
      ) : error ? (
        <p className="odv2-anexos-status odv2-anexos-status--error" role="alert">
          {error}
        </p>
      ) : files.length === 0 ? (
        <p className="odv2-anexos-status">Nenhum anexo nesta OS.</p>
      ) : (
        <div className="odv2-anexos">
          <div className="odv2-anexos-thumbs" role="list">
            {files.map((file, i) => (
              <button
                key={file.name}
                type="button"
                role="listitem"
                className={`odv2-anexos-thumb${i === selected ? ' odv2-anexos-thumb--active' : ''}`}
                aria-label={file.name}
                title={file.name}
                onClick={() => {
                  setSelected(i);
                  setExpanded(false);
                }}
              >
                {file.isImage ? (
                  <img src={file.url} alt="" />
                ) : (
                  <FileText size={18} strokeWidth={1.5} aria-hidden />
                )}
              </button>
            ))}
          </div>

          {current ? (
            <div className="odv2-anexos-main">
              <div className="odv2-anexos-preview">
                <button
                  type="button"
                  className="odv2-anexos-expand"
                  aria-label="Ampliar anexo"
                  onClick={() => setExpanded(true)}
                >
                  <Expand size={14} strokeWidth={2} aria-hidden />
                </button>
                {current.isImage ? (
                  <img src={current.url} alt={current.name} />
                ) : String(current.ext).toLowerCase() === 'pdf' ? (
                  <iframe title={current.name} src={current.url} />
                ) : (
                  <div className="odv2-anexos-preview-fallback">
                    <FileText size={40} strokeWidth={1.5} aria-hidden />
                    <span>{current.name}</span>
                    <a href={current.url} target="_blank" rel="noreferrer">
                      Abrir arquivo
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
      {zoomNode}
    </div>
  );
}
